/**
 * Questa è la classe per il Repository che riguarda i servizi di lookup per i menu a tendina
 * Quindi tutte le richieste ai servizi che c'entrano con campi tipo menu a tendina o che vengono
 * usati molto andrebbero messi qui
 *
 */

import axios, { AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { getResponseMessage, IBaseRequest, IBooleanResponse, INumericResponse, IStringResponse, IStringRequest } from "core/interfaces/data/Base";

import {
	IGetComuniResponse,
	IGetComuniRequest,
	IComune,
	IGetMunicipiRequest,
	IGetMunicipiResponse,
	IMunicipio,
	IGetCittadinanzeRequest,
	ICittadinanza,
	IGetCittadinanzeResponse,
	IStatoCivile,
	IGetStatiCiviliResponse,
	IGetLegamiUtenteResponse,
	ILegameUtente,
	IGetComuniByCfRequest,
	IGetComuniByCfResponse,
} from "core/interfaces/data/IGeneralRequest";
import { ICheckFiscalCodeValidityRequest } from "core/interfaces/data/IValidatorsRequest";

// questo serve per fare la richiesta una seconda volta se in caso la prima non va a buon fine
axiosRetry(axios, {
	retries: 2,
});

// serve per settare i cookie in risposta
axios.defaults.withCredentials = true;

class GeneralRepository {
	/**
	 * Metodo che ritorna i comuni
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async getComuni(props: IGetComuniRequest): Promise<IComune[] | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/CitiesGetList`;

			const res = await axios.post<IGetComuniRequest, AxiosResponse<IGetComuniResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(res.data.Errors));
			}

			const comuni: IComune[] = JSON.parse(res.data.Comuni);

			return comuni;
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Metodo che ritorna i municipi
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async getMunicipi(props: IGetMunicipiRequest): Promise<IMunicipio[] | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/TownHallsGetList`;

			const res = await axios.post<IGetMunicipiRequest, AxiosResponse<IGetMunicipiResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			const municipi: IMunicipio[] = JSON.parse(res.data.Municipi);

			return municipi;
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Metodo che ritorna le varie cittadinanze
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async getCittadinanze(props: IGetCittadinanzeRequest): Promise<ICittadinanza[] | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/CitizenshipsGetList`;

			const res = await axios.post<IGetCittadinanzeRequest, AxiosResponse<IGetCittadinanzeResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			const cittadinanze: ICittadinanza[] = JSON.parse(res.data.Cittadinanze);

			return cittadinanze;
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Metodo che ritorna gli stati civili
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async getStatiCivili(props: IBaseRequest): Promise<IStatoCivile[] | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/CivilStatusesGetList`;

			const res = await axios.post<IBaseRequest, AxiosResponse<IGetStatiCiviliResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(getResponseMessage(res.data.Errors));
			}

			const statiCivili: IStatoCivile[] = JSON.parse(res.data.StatiCivili);

			return statiCivili;
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Metodo che prende tutti i possibili legami dell'utente con il richiedente
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async getLegamiUtente() {
		const url = `${window.CONFIG.REACT_APP_WS_HSB}/ApplicantUserBondsGet`;

		const res = await axios.get<IGetLegamiUtenteResponse>(url);

		if (res.data.Errors) {
			throw new Error(getResponseMessage(res.data.Errors));
		}

		const familiari: ILegameUtente[] = JSON.parse(res.data.LegamiUtenteRichiedente);

		return familiari;
	}

	/**
	 * Metodo che ritorna il comune dato il suo codice fiscale
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async getComuniByCf(props: IGetComuniByCfRequest): Promise<IComune | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/CityGetListByFiscalCode`;

			const res = await axios.post<IGetComuniByCfRequest, AxiosResponse<IGetComuniByCfResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(res.data.Errors));
			}

			const comuni: IComune = JSON.parse(res.data.Comune);

			return comuni;
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Metodo che verifica la presenza di un assistito all'interno del sistema in base al suo codice fiscale
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async checkAssistedExists(props: IStringRequest): Promise<boolean | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/CheckAssistedExists`;

			const res = await axios.post<IStringRequest, AxiosResponse<IBooleanResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(res.data.Errors));
			}
			return res.data.Value;
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Metodo che verifica la presenza di un account di login all'interno del sistema in base al suo codice fiscale
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async checkLoginExistsByCF(props: IStringRequest): Promise<boolean | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/CheckLoginExistsByCF`;

			const res = await axios.post<IStringRequest, AxiosResponse<IBooleanResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(res.data.Errors));
			}
			return res.data.Value;
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Metodo che verifica la presenza di una persona di riferimento all'interno del sistema in base al suo codice fiscale
	 * Le props, ovvero i parametri che servono per fare la richiesta li dichiaro
	 * con una interfaccia che contiene i campi che servono per chiamare il servizio
	 * Nella richiesta di Axios gli do il tipo così puoi usare l'intellisense
	 * Rispetto ai dati che hai messo nella Response crei l'interfaccia che mappa quei campi
	 */
	async checkPersRifExistsByCF(props: IStringRequest): Promise<boolean | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/checkPersRifExistsByCF`;

			const res = await axios.post<IStringRequest, AxiosResponse<IBooleanResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(getResponseMessage(res.data.Errors)));
			}
			return res.data.Value;
		} catch (err) {
			console.log(err);
		}
	}

	async passwordMinLenghtGet() {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/passwordMinLenghtGet`;

			const res = await axios.get<IStringResponse>(url);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(getResponseMessage(res.data.Errors)));
			}
			return res.data.Value;
		} catch (err) {
			console.log(err);
			return "0";
		}
	}

	async HSBTipoRicercaRisorsaUmana() {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/HSBTipoRicercaRisorsaUmana`;

			const res = await axios.get<INumericResponse>(url);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(getResponseMessage(res.data.Errors)));
			}
			return res.data.Value;
		} catch (err) {
			console.log(err);
			return 0;
		}
	}
	async checkFiscalCodeValidity(props: ICheckFiscalCodeValidityRequest): Promise<boolean | undefined> {
		try {
			const url = `${window.CONFIG.REACT_APP_WS_HSB}/CheckFiscalCodeValidity`;

			const res = await axios.post<ICheckFiscalCodeValidityRequest, AxiosResponse<IBooleanResponse>>(url, props);

			if (res.data.Errors) {
				throw new Error(JSON.stringify(res.data.Errors));
			}
			return res.data.Value;
		} catch (err) {
			console.log(err);
		}
	}
}
var GeneralRep =new GeneralRepository()
export default GeneralRep;
